<template>
  <ModalBasicTailwind
    id="predix-report-dialog"
    size="2xl"
    :modal-open="modalOpen"
    :title="dialogTitle"
    :subtitle="dialogSubtitle"
    @close-modal="close"
  >
    <!-- Step: select properties -->
    <div v-if="step === 'select'" class="px-5 py-4">
      <div
        v-if="!categoriesWithItems.length"
        class="py-10 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        No predicted properties available to report.
      </div>
      <template v-else>
        <div class="flex items-center justify-between gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-gray-700/60">
          <button
            type="button"
            class="predix-report-select-all"
            @click="toggleSelectAll(!allSelected)"
          >
            <i
              class="predix-report-fa"
              :class="allSelected ? 'fa-solid fa-square-check' : (someSelected ? 'fa-solid fa-square-minus' : 'fa-regular fa-square')"
              aria-hidden="true"
            />
            Select all
          </button>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            {{ selectedCount }} selected
          </span>
        </div>

        <div class="predix-report-cat-list">
          <section
            v-for="category in categoriesWithItems"
            :key="category.id"
            class="predix-report-cat"
          >
            <button
              type="button"
              class="predix-report-cat-header"
              :class="{ 'predix-report-locked': isCategoryLocked(category) }"
              :disabled="isCategoryLocked(category)"
              :title="isCategoryLocked(category) ? 'Alloy Composition is always included in export' : undefined"
              @click="toggleCategory(category, !isCategoryFullySelected(category))"
            >
              <i
                class="predix-report-fa"
                :class="isCategoryFullySelected(category)
                  ? 'fa-solid fa-square-check'
                  : (isCategoryPartiallySelected(category) ? 'fa-solid fa-square-minus' : 'fa-regular fa-square')"
                aria-hidden="true"
              />
              <span class="predix-report-cat-name">{{ category.name }}</span>
              <span v-if="isCategoryLocked(category)" class="predix-report-locked-label">Required</span>
              <span class="predix-report-cat-count">{{ category.items.length }}</span>
            </button>
            <ul class="predix-report-item-list">
              <li
                v-for="item in category.items"
                :key="itemKey(category.id, item)"
                class="predix-report-item"
              >
                <button
                  type="button"
                  class="predix-report-item-btn"
                  :class="{ 'predix-report-locked': isCategoryLocked(category) }"
                  :disabled="isCategoryLocked(category)"
                  :title="isCategoryLocked(category) ? 'Alloy Composition is always included in export' : undefined"
                  @click="toggleItem(category.id, item, !isItemSelected(category.id, item))"
                >
                  <i
                    class="predix-report-fa"
                    :class="isItemSelected(category.id, item) ? 'fa-solid fa-square-check' : 'fa-regular fa-square'"
                    aria-hidden="true"
                  />
                  <span class="predix-report-item-name">{{ item.name }}</span>
                  <span class="predix-report-item-value">{{ formatValue(item.value) }}</span>
                </button>
              </li>
            </ul>
          </section>
        </div>
      </template>
    </div>

    <!-- Step: name report -->
    <div v-else-if="step === 'name'" class="px-5 py-4 space-y-4">
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ selectedCount }} item{{ selectedCount === 1 ? '' : 's' }} will be included.
      </p>
      <div class="predix-report-name-field">
        <CTailwindInput
          id="predix-report-name"
          label="Report name"
          required
          :value="reportName"
          type="text"
          name="reportName"
          placeholder="e.g. Main DB-Report-38471"
          @input="reportName = $event"
        />
      </div>
      <div class="predix-report-password">
        <button
          type="button"
          class="predix-report-password-toggle"
          @click="togglePasswordProtect(!passwordProtect)"
        >
          <i
            class="predix-report-fa"
            :class="passwordProtect ? 'fa-solid fa-square-check' : 'fa-regular fa-square'"
            aria-hidden="true"
          />
          <span>Protect with password</span>
          <span class="predix-report-password-hint">Recommended</span>
        </button>
        <div v-if="passwordProtect" class="predix-report-password-fields">
          <CTailwindInput
            id="predix-report-password"
            label="Password"
            required
            :value="reportPassword"
            type="text"
            name="reportPassword"
            autocomplete="off"
            placeholder="Share this with viewers"
            @input="reportPassword = $event"
          />
          <button
            type="button"
            class="predix-report-password-regen"
            title="Generate a new password"
            @click="reportPassword = generateRandomPassword()"
          >
            <i class="fa-solid fa-arrows-rotate" aria-hidden="true" />
            Regenerate
          </button>
        </div>
      </div>
      <p v-if="submitError" class="text-sm text-red-600 dark:text-red-400">
        {{ submitError }}
      </p>
    </div>

    <!-- Step: share link -->
    <div v-else-if="step === 'done'" class="px-5 py-4 space-y-4">
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Report created. Open the page below.
      </p>
      <div class="flex gap-2 items-stretch">
        <input
          ref="linkInput"
          type="text"
          readonly
          class="predix-report-link-input flex-1 min-w-0"
          :value="reportLink"
          @focus="$event.target.select()"
        />
        <button
          type="button"
          class="predix-report-icon-btn"
          title="Open in new tab"
          aria-label="Open in new tab"
          @click="openReportPage"
        >
          <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="predix-report-icon-btn"
          :title="copiedLink ? 'Copied' : 'Copy link'"
          :aria-label="copiedLink ? 'Copied' : 'Copy link'"
          @click="copyLink"
        >
          <i
            class="fa-solid"
            :class="copiedLink ? 'fa-check' : 'fa-copy'"
            aria-hidden="true"
          />
        </button>
      </div>
      <div v-if="createdPassword" class="predix-report-password-row">
        <div class="flex gap-2 items-stretch">
          <input
            ref="passwordInput"
            type="text"
            readonly
            class="predix-report-link-input flex-1 min-w-0"
            :value="createdPassword"
            @focus="$event.target.select()"
          />
          <button
            type="button"
            class="predix-report-icon-btn"
            :title="copiedPassword ? 'Copied' : 'Copy password'"
            :aria-label="copiedPassword ? 'Copied' : 'Copy password'"
            @click="copyPassword"
          >
            <i
              class="fa-solid"
              :class="copiedPassword ? 'fa-check' : 'fa-copy'"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>

    <div class="px-5 py-4 border-t border-gray-200 dark:border-gray-700/60">
      <div class="flex flex-wrap justify-end gap-2">
        <CTailwindBtn type="button" variant="outline" @click="close">
          {{ step === 'done' ? 'Close' : 'Cancel' }}
        </CTailwindBtn>
        <CTailwindBtn
          v-if="step === 'select'"
          type="button"
          variant="primary"
          :disabled="selectedCount === 0"
          @click="goToNameStep"
        >
          Submit
        </CTailwindBtn>
        <CTailwindBtn
          v-else-if="step === 'name'"
          type="button"
          variant="primary"
          :disabled="!canCreate || creating"
          @click="create"
        >
          {{ creating ? 'Creating…' : 'Create report' }}
        </CTailwindBtn>
        <CTailwindBtn
          v-else-if="step === 'done'"
          type="button"
          variant="primary"
          @click="shareReport"
        >
          <i class="fa-solid fa-share-nodes" aria-hidden="true" />
          {{ sharedCopied ? 'Copied' : 'Share report' }}
        </CTailwindBtn>
      </div>
    </div>
  </ModalBasicTailwind>
</template>

<script>
import ModalBasicTailwind from '@/components/modal/ModalBasicTailwind.vue'
import CTailwindBtn from '@/components/Vuetify/CTailwindBtn.vue'
import CTailwindInput from '@/components/Vuetify/CTailwindInput.vue'
import { createReport, extractReportId } from '@/api/reports'
import { createCatKeys } from '@/api/catkeys'
import { smartPruneNumber } from '@/utils/number'
import { filterPositivePredictedResults } from '@/utils/predixExploreResult'

export default {
  name: 'PredixReportDialog',
  components: {
    ModalBasicTailwind,
    CTailwindBtn,
    CTailwindInput,
  },
  props: {
    modalOpen: {
      type: Boolean,
      default: false,
    },
    /** [{ id, name, includeZero?, items: [{ id, name, value }] }] */
    categories: {
      type: Array,
      default: () => [],
    },
    /** Selected PrediX database display name used in the default report title */
    databaseName: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    step: 'select',
    selectedKeys: [],
    reportName: '',
    reportLink: '',
    passwordProtect: true,
    reportPassword: '',
    createdPassword: '',
    creating: false,
    submitError: '',
    copiedLink: false,
    copiedPassword: false,
    sharedCopied: false,
  }),
  computed: {
    categoriesWithItems() {
      return (this.categories || [])
        .map((cat) => {
          const rawItems = cat.items || []
          const items = cat.includeZero
            ? rawItems.filter((item) => item != null && item.value !== undefined && item.value !== null && item.value !== '')
            : filterPositivePredictedResults(rawItems)
          return {
            id: cat.id,
            name: cat.name,
            includeZero: !!cat.includeZero,
            items,
          }
        })
        .filter((cat) => cat.items.length > 0)
    },
    allItemKeys() {
      const keys = []
      for (const cat of this.categoriesWithItems) {
        for (const item of cat.items) {
          keys.push(this.itemKey(cat.id, item))
        }
      }
      return keys
    },
    selectedCount() {
      return this.selectedKeys.length
    },
    allSelected() {
      return this.allItemKeys.length > 0 && this.selectedKeys.length === this.allItemKeys.length
    },
    someSelected() {
      return this.selectedKeys.length > 0
    },
    canCreate() {
      if (!this.reportName.trim() || this.selectedCount === 0) return false
      if (this.passwordProtect && !this.reportPassword.trim()) return false
      return true
    },
    dialogTitle() {
      if (this.step === 'done') return 'Report ready'
      return 'PrediX™ Report Export'
    },
    dialogSubtitle() {
      if (this.step === 'select') {
        return 'Select alloy prediction properties to include.'
      }
      if (this.step === 'name') {
        return 'You can change the name and password of the export.'
      }
      return ''
    },
  },
  watch: {
    modalOpen(open) {
      if (open) {
        this.resetState()
      }
    },
  },
  methods: {
    itemKey(categoryId, item) {
      return `${categoryId}::${item.id != null ? item.id : item.name}`
    },
    isCategoryLocked(category) {
      return String(category && category.id) === 'composition'
    },
    compositionKeys() {
      const composition = this.categoriesWithItems.find((c) => this.isCategoryLocked(c))
      if (!composition) return []
      return composition.items.map((item) => this.itemKey(composition.id, item))
    },
    formatValue(value) {
      const n = typeof value === 'number' ? value : parseFloat(value)
      if (!Number.isFinite(n)) return value
      return smartPruneNumber(n)
    },
    generateRandomPassword(length = 12) {
      const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
      const bytes = new Uint8Array(length)
      if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes)
      } else {
        for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
      }
      let out = ''
      for (let i = 0; i < length; i += 1) {
        out += alphabet[bytes[i] % alphabet.length]
      }
      return out
    },
    /** 5-digit number in 10000–99999 (never starts with 0) */
    generateExportSuffix() {
      return String(Math.floor(Math.random() * 90000) + 10000)
    },
    generateDefaultReportName() {
      const db = String(this.databaseName || '').trim() || 'PrediX'
      return `${db}-Report-${this.generateExportSuffix()}`
    },
    togglePasswordProtect(on) {
      this.passwordProtect = !!on
      if (this.passwordProtect && !this.reportPassword.trim()) {
        this.reportPassword = this.generateRandomPassword()
      }
    },
    resetState() {
      this.step = 'select'
      this.selectedKeys = []
      this.reportName = ''
      this.reportLink = ''
      this.passwordProtect = true
      this.reportPassword = this.generateRandomPassword()
      this.createdPassword = ''
      this.creating = false
      this.submitError = ''
      this.copiedLink = false
      this.copiedPassword = false
      this.sharedCopied = false
      this.$nextTick(() => {
        this.ensureCompositionSelected()
      })
    },
    ensureCompositionSelected() {
      const locked = this.compositionKeys()
      if (!locked.length) return
      this.setSelectedKeys([...this.selectedKeys, ...locked])
    },
    close() {
      this.$emit('close-modal')
    },
    isItemSelected(categoryId, item) {
      return this.selectedKeys.includes(this.itemKey(categoryId, item))
    },
    isCategoryFullySelected(category) {
      if (!category.items.length) return false
      return category.items.every((item) => this.isItemSelected(category.id, item))
    },
    isCategoryPartiallySelected(category) {
      const any = category.items.some((item) => this.isItemSelected(category.id, item))
      return any && !this.isCategoryFullySelected(category)
    },
    setSelectedKeys(keys) {
      const locked = this.compositionKeys()
      this.selectedKeys = [...new Set([...locked, ...keys])]
    },
    toggleSelectAll(checked) {
      if (checked) {
        this.setSelectedKeys(this.allItemKeys)
      } else {
        // Keep composition selected; clear only optional categories
        this.setSelectedKeys([])
      }
    },
    toggleCategory(category, checked) {
      if (this.isCategoryLocked(category)) return
      const catKeys = category.items.map((item) => this.itemKey(category.id, item))
      if (checked) {
        this.setSelectedKeys([...this.selectedKeys, ...catKeys])
      } else {
        this.setSelectedKeys(this.selectedKeys.filter((k) => !catKeys.includes(k)))
      }
    },
    toggleItem(categoryId, item, checked) {
      const category = this.categoriesWithItems.find((c) => String(c.id) === String(categoryId))
      if (category && this.isCategoryLocked(category)) return
      const key = this.itemKey(categoryId, item)
      if (checked) {
        this.setSelectedKeys([...this.selectedKeys, key])
      } else {
        this.setSelectedKeys(this.selectedKeys.filter((k) => k !== key))
      }
    },
    goToNameStep() {
      this.ensureCompositionSelected()
      if (this.selectedCount === 0) return
      this.submitError = ''
      if (!this.reportName.trim()) {
        this.reportName = this.generateDefaultReportName()
      }
      this.step = 'name'
    },
    buildItemsPayload() {
      this.ensureCompositionSelected()
      const items = []
      const seen = new Set()
      for (const cat of this.categoriesWithItems) {
        for (const item of cat.items) {
          const key = this.itemKey(cat.id, item)
          const locked = this.isCategoryLocked(cat)
          if ((!locked && !this.selectedKeys.includes(key)) || seen.has(key)) continue
          seen.add(key)
          const formatted = String(this.formatValue(item.value))
          const catName = String((item && item.cat) || cat.name || '').trim() || 'Other'
          const keyName = String(item.name)
          items.push({
            cat: catName,
            key: keyName,
            value: formatted,
            name: keyName,
          })
        }
      }
      return items
    },
    create() {
      if (!this.canCreate || this.creating) return
      this.creating = true
      this.submitError = ''
      const items = this.buildItemsPayload()
      const payload = {
        name: this.reportName.trim(),
        items,
      }
      const username = this.$store && this.$store.state && this.$store.state.Username
      if (username) payload.username = String(username)
      const password = this.passwordProtect ? this.reportPassword.trim() : ''
      if (password) {
        payload.password = password
      }
      createReport(
        payload,
        (res) => {
          // Also register selected keys under their categories on the backend.
          const catKeyPayloads = items.map((item) => ({
            cat: item.cat,
            key: item.key,
            value: item.value,
          }))
          createCatKeys(catKeyPayloads, () => {
            this.creating = false
            const id = extractReportId(res)
            if (!id) {
              this.submitError = 'Report created but no report id was returned.'
              return
            }
            const route = this.$router.resolve({ name: 'PredixPublicReport', params: { id: String(id) } })
            this.reportLink = `${window.location.origin}${route.href}`
            this.createdPassword = password || (res && res.password) || ''
            this.step = 'done'
          })
        },
        (err) => {
          this.creating = false
          const msg =
            (err && err.data && (err.data.detail || err.data.message || err.data.error)) ||
            'Could not create the report. Please try again.'
          this.submitError = typeof msg === 'string' ? msg : 'Could not create the report. Please try again.'
        }
      )
    },
    async copyText(text, flagKey, inputRef) {
      if (!text) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else if (inputRef && this.$refs[inputRef]) {
          this.$refs[inputRef].select()
          document.execCommand('copy')
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this[flagKey] = true
        setTimeout(() => {
          this[flagKey] = false
        }, 2000)
      } catch (e) {
        if (inputRef && this.$refs[inputRef]) {
          this.$refs[inputRef].select()
        }
      }
    },
    copyLink() {
      return this.copyText(this.reportLink, 'copiedLink', 'linkInput')
    },
    copyPassword() {
      return this.copyText(this.createdPassword, 'copiedPassword', 'passwordInput')
    },
    buildShareText() {
      const name = String(this.reportName || '').trim() || 'PrediX report'
      const link = String(this.reportLink || '').trim()
      const password = String(this.createdPassword || '').trim()

      let text =
        `This is the report "${name}".\n\n` +
        `You can access it using the following link:\n${link}\n`

      if (password) {
        text += `\nPlease use the password below to open the report:\n${password}\n`
      }

      return text
    },
    shareReport() {
      return this.copyText(this.buildShareText(), 'sharedCopied')
    },
    openReportPage() {
      if (!this.reportLink) return
      window.open(this.reportLink, '_blank', 'noopener,noreferrer')
    },
  },
}
</script>

<style scoped>
.predix-report-select-all {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  user-select: none;
}

html.dark .predix-report-select-all {
  color: #f3f4f6;
}

.predix-report-cat-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-height: min(420px, 55vh);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.25rem;
}

.predix-report-cat {
  flex: 0 0 auto;
  min-height: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: visible;
  background: #fff;
}

html.dark .predix-report-cat {
  border-color: rgb(255 255 255 / 0.12);
  background: transparent;
}

.predix-report-cat-header,
.predix-report-item-btn {
  box-sizing: border-box;
  display: flex !important;
  align-items: center;
  width: 100%;
  margin: 0;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: inherit;
}

.predix-report-cat-header {
  gap: 0.75rem;
  padding: 1rem;
  background: #f9fafb;
}

html.dark .predix-report-cat-header {
  background: rgb(17 24 39 / 0.45);
}

.predix-report-cat-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}

html.dark .predix-report-cat-name {
  color: #f3f4f6;
}

.predix-report-cat-count {
  flex: 0 0 auto;
  font-size: 0.75rem;
  color: #6b7280;
}

html.dark .predix-report-cat-count {
  color: #9ca3af;
}

.predix-report-locked-label {
  flex: 0 0 auto;
  margin-left: auto;
  margin-right: 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--v-primary-base, #0393a8);
}

.predix-report-cat-header.predix-report-locked .predix-report-cat-count {
  margin-left: 0;
}

.predix-report-locked {
  cursor: default !important;
  opacity: 0.92;
}

.predix-report-item-btn.predix-report-locked:hover {
  background: transparent;
}

.predix-report-cat-header.predix-report-locked:disabled,
.predix-report-item-btn.predix-report-locked:disabled {
  opacity: 1;
  cursor: default;
}

.predix-report-item-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.predix-report-item {
  border-top: 1px solid #f3f4f6;
}

html.dark .predix-report-item {
  border-top-color: rgb(255 255 255 / 0.08);
}

.predix-report-item-btn {
  gap: 0.75rem;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  background: transparent;
}

.predix-report-item-btn:hover {
  background: #f9fafb;
}

html.dark .predix-report-item-btn:hover {
  background: rgb(31 41 55 / 0.45);
}

.predix-report-item-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  color: #1f2937;
}

html.dark .predix-report-item-name {
  color: #e5e7eb;
}

.predix-report-item-value {
  flex: 0 0 auto;
  font-size: 0.875rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: #374151;
}

html.dark .predix-report-item-value {
  color: #d1d5db;
}

.predix-report-fa {
  flex: 0 0 auto;
  width: 1.15rem;
  font-size: 1.15rem;
  line-height: 1;
  text-align: center;
  color: var(--v-primary-base, #0393a8);
}

.predix-report-link-input {
  box-sizing: border-box;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #1f2937;
  font-size: 0.875rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

html.dark .predix-report-link-input {
  border-color: rgb(255 255 255 / 0.12);
  background: rgb(17 24 39 / 0.6);
  color: #e5e7eb;
}

.predix-report-icon-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.5rem;
  min-height: 2.5rem;
  margin: 0;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #fff;
  color: #374151;
  cursor: pointer;
  font-size: 0.95rem;
  line-height: 1;
}

.predix-report-icon-btn:hover {
  background: #f3f4f6;
  color: var(--v-primary-base, #0393a8);
}

html.dark .predix-report-icon-btn {
  border-color: rgb(255 255 255 / 0.15);
  background: transparent;
  color: #d1d5db;
}

html.dark .predix-report-icon-btn:hover {
  background: rgb(255 255 255 / 0.06);
  color: var(--v-primary-base, #0393a8);
}

.predix-report-password-row {
  margin-top: 1.25rem;
}

.predix-report-name-field {
  margin-bottom: 1rem;
}

.predix-report-password {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.85rem 0.9rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
}

html.dark .predix-report-password {
  border-color: rgb(255 255 255 / 0.12);
  background: rgb(17 24 39 / 0.35);
}

.predix-report-password-toggle {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  user-select: none;
}

html.dark .predix-report-password-toggle {
  color: #f3f4f6;
}

.predix-report-password-hint {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
}

html.dark .predix-report-password-hint {
  color: #9ca3af;
}

.predix-report-password-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.predix-report-password-regen {
  box-sizing: border-box;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.35rem 0.55rem;
  border: 1px solid #d1d5db;
  border-radius: 0.4rem;
  background: #fff;
  color: #374151;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.predix-report-password-regen:hover {
  background: #f3f4f6;
}

html.dark .predix-report-password-regen {
  border-color: rgb(255 255 255 / 0.15);
  background: transparent;
  color: #d1d5db;
}

html.dark .predix-report-password-regen:hover {
  background: rgb(255 255 255 / 0.06);
}
</style>
